import React, { useState } from 'react'
import { segmentTrack } from '@src/functions/segment'
import classes from '@src/styles/disposal/call-to-book.module.css'

/** Single number used for every service today. */
export const defaultCampaignPhoneNumber =
  process.env.NEXT_PUBLIC_CTA_PHONE_NUMBER ?? '415-417-0239'

/**
 * Per-`formId` overrides. Empty today (one number covers all services) — kept as
 * the single source of truth so numbers can diverge per service later. Shared by
 * other sections (e.g. FAQ) that surface the same phone CTA.
 */
export const campaignPhoneNumbers: Record<string, string> = {}

export const getCampaignPhoneNumber = (formId?: string) =>
  (formId && campaignPhoneNumbers[formId]) || defaultCampaignPhoneNumber

export type CallToBookProps = {
  /** Service slug — drives analytics payload + per-service number overrides. */
  formId?: string
  /** Where this CTA is rendered, for analytics (`form` banner, `header`, or `chatbot` panel). */
  from?: 'form' | 'header' | 'chatbot'
}

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const PhoneIcon = () => (
  <svg {...iconProps}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
  </svg>
)

const CopyIcon = () => (
  <svg {...iconProps}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

const CheckIcon = () => (
  <svg {...iconProps}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

/**
 * "Still have questions? Call us:" banner — a tappable phone number with a
 * copy-to-clipboard button. Rendered above the Lindy chatbot / lead form.
 */
function CallToBook({ formId, from = 'form' }: CallToBookProps) {
  const [copied, setCopied] = useState(false)
  const phoneNumber = getCampaignPhoneNumber(formId)

  const handleCall = () => {
    segmentTrack('Disposal Flow - Call to Book', { formId, phoneNumber, from })
    segmentTrack('Disposal Flow - Clicked Phone Number', { formId, phoneNumber, from })
    if (typeof window !== 'undefined') {
      window.open(`tel:${phoneNumber}`)
    }
  }

  const handleCopy = async () => {
    segmentTrack('Disposal Flow - Call to Book', { formId, phoneNumber, from })
    segmentTrack('Disposal Flow - Copied Phone Number', { formId, phoneNumber, from })
    try {
      await navigator.clipboard.writeText(phoneNumber)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (insecure context / denied) — no-op.
    }
  }

  return (
    <div className={classes.banner}>
      <span className={classes.label}>
        <span className={classes.labelDesktop}>Still have questions? Call us:</span>
        <span className={classes.labelMobile}>Still have questions? Call us:</span>
      </span>
      <div className={classes.actions}>
        <button type="button" className={classes.phoneButton} onClick={handleCall}>
          <PhoneIcon />
          <span className={classes.phoneNumber}>{phoneNumber}</span>
        </button>
        <button
          type="button"
          className={classes.copyButton}
          onClick={handleCopy}
          aria-label={copied ? 'Phone number copied' : 'Copy phone number'}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      </div>
    </div>
  )
}

export default CallToBook
